import { useCallback, useMemo } from 'react';
import { FieldMask } from '@weave/schema-gen-ts/dist/google/protobuf/field_mask.pb';
import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { List, ListListsRequest } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { useCreateList, useDeleteList, useUpdateList, useListLists, useGetList } from '@frontend/api-bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { useAlert } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { CreateListView } from '../types';

// TODO: Modify this to work with segments and how bulk email currently works
// TODO: Add a mechanism that disables these queries until the user navigates to the contact lists tab. No need to fetch them on the main screen.
export const useListsView = () => {
  const { t } = useTranslation('bulk-messaging');
  const { error: alertError } = useAlert();

  const {
    campaignType,
    expectModeToBe,
    listToUpdate,
    listsSearchQuery,
    locationIds,
    mode,
    restoreRecipients,
    selectedListIds,
    selectedRecipients,
    setListToUpdate,
    setMode,
    setSelectedListIds,
    setSelectedRecipients,
    startLoading,
    stashRecipients,
    stopLoading,
  } = useAudienceTray();
  const locationId = locationIds[0];

  const listListsRequest: ListListsRequest = {
    locationId,
    pageSize: 25,
    searchQuery: listsSearchQuery,
    types: [campaignType],
  };

  const listsQueryResult = useListLists(listListsRequest);
  const allListsQueryResult = useListLists({ ...listListsRequest, searchQuery: undefined });

  const lists = useMemo(
    () => listsQueryResult.data?.pages.flatMap((page) => page.lists) ?? [],
    [listsQueryResult.data]
  );

  const allLists = useMemo(
    () => allListsQueryResult.data?.pages.flatMap((page) => page.lists) ?? [],
    [allListsQueryResult.data]
  );

  const { mutateAsync: createContactList } = useCreateList(listListsRequest);
  const { mutateAsync: deleteContactList } = useDeleteList(listListsRequest);
  const { mutateAsync: updateContactList } = useUpdateList(listListsRequest);
  const getList = useGetList();

  const isListSelected = useCallback(
    (list: List) => {
      if (!list.listId) return false;
      return selectedListIds.includes(list.listId);
    },
    [selectedListIds]
  );

  const selectList = useCallback(
    (listId: string) => {
      setSelectedListIds((prevIds) => [...prevIds, listId]);
    },
    [setSelectedListIds]
  );

  const deselectList = useCallback(
    (listId: string) => {
      setSelectedListIds((prevIds) => prevIds.filter((selectedListId) => selectedListId !== listId));
    },
    [setSelectedListIds]
  );

  const toggleList = useCallback(
    (list: List) => {
      if (!list.listId) return;
      if (isListSelected(list)) {
        deselectList(list.listId);
      } else {
        selectList(list.listId);
      }
    },
    [isListSelected, deselectList, selectList]
  );

  const createList = useCallback(
    async (list: List) => {
      const response = await createContactList({ list });
      if (response.listId) selectList(response.listId);
    },
    [createContactList, selectList]
  );

  const deleteList = useCallback(
    async (listId: string) => {
      await deleteContactList({ listId });
      deselectList(listId);
    },
    [deleteContactList, deselectList]
  );

  const renameList = useCallback(
    async (list: List) => {
      await updateContactList({ list, updateMask: new FieldMask({ paths: ['list.name'] }) });
    },
    [updateContactList]
  );

  const updateListRecipients = useCallback(
    async (list: List) => {
      await updateContactList({
        list,
        updateMask: new FieldMask({ paths: ['list.recipients', 'list.recipientCount'] }),
      });
    },
    [updateContactList]
  );

  const selectedListsRecipientsCount = useMemo(
    () =>
      locationIds.length === 1
        ? selectedListIds
            .map((listId) => allLists.find((list) => list.listId === listId)?.recipientCount ?? 0)
            .reduce((acc, count) => acc + count, 0)
        : 0,
    [locationIds, selectedListIds, allLists]
  );

  const startCreateListFrom = useCallback(
    (fromView: CreateListView) => {
      expectModeToBe(['lists', 'recipients'], 'startCreateList');

      // Saved Lists > Create List Button: Stash recipient selection. Go to All Contacts tab.
      if (mode === 'lists' && fromView === 'lists') {
        stashRecipients();
        setMode('recipients-select-list');
      }

      // All Contacts > Save List Button: Go to Saved Lists tab.
      if (mode === 'recipients' && fromView === 'recipients') {
        setMode('lists-create-from-recipients');
      }

      // Saved Lists > Upload CSV List Button: Stash recipients. Go to Upload CSV view.
      if (mode === 'lists' && fromView === 'upload') {
        stashRecipients();
        setMode('upload');
      }
    },
    [mode, expectModeToBe, stashRecipients, setMode]
  );

  const advanceCreateList = useCallback(
    (newRecipients?: Recipient[]) => {
      expectModeToBe(['recipients-select-list', 'upload'], 'advanceCreateList');

      // All Contacts > Save New List Button: Advance to naming selected recipients list on Saved Lists tab
      if (mode === 'recipients-select-list') {
        setMode('lists-create-from-lists');
      }

      // Upload CSV > Successful CSV Upload: Advance to naming uploaded list on Saved Lists tab
      if (mode === 'upload') {
        if (newRecipients) {
          setSelectedRecipients(newRecipients);
        }
        setMode('lists-create-from-upload');
      }
    },
    [mode, expectModeToBe, setMode, setSelectedRecipients]
  );

  const cancelCreateList = useCallback(() => {
    expectModeToBe(
      [
        'lists-create-from-lists',
        'lists-create-from-recipients',
        'lists-create-from-upload',
        'recipients-select-list',
        'upload',
      ],
      'cancelCreateList'
    );

    // All Contacts / Create New List > Cancel Button: Restore previous recipient selection. Go back to the Saved Lists tab
    if (mode === 'recipients-select-list') {
      restoreRecipients();
      setMode('lists');
    }

    // Upload CSV > Cancel Button: Cancel from the Upload CSV view. Go back to the Saved Lists tab.
    if (mode === 'upload') {
      restoreRecipients();
      setMode('lists');
    }

    // Saved Lists > New List: Cancel after creating a new list from the Saved Lists tab, but before save. Stay on the Saved Lists tab.
    if (mode === 'lists-create-from-lists') {
      restoreRecipients();
      setMode('lists');
    }

    // Saved Lists > New List: Cancel after creating a new list from the All Contacts tab, but before save. Go back to the All Contacts tab.
    if (mode === 'lists-create-from-recipients') {
      setMode('recipients');
    }

    // Saved Lists > New List: Cancel after after CSV upload, but before save. Stay on the Saved Lists tab.
    if (mode === 'lists-create-from-upload') {
      restoreRecipients();
      setMode('lists');
    }
  }, [mode, expectModeToBe, restoreRecipients, setMode]);

  const finishCreateList = useCallback(
    async (listName: string) => {
      expectModeToBe(
        ['lists-create-from-lists', 'lists-create-from-recipients', 'lists-create-from-upload'],
        'finishCreateList'
      );
      startLoading();

      try {
        // Saved Lists > New List Save Button: Save after creating a new list from the Saved Lists tab. Stay on the Saved Lists tab.
        if (mode === 'lists-create-from-lists') {
          await createList({ name: listName, recipients: selectedRecipients, locationId, type: campaignType });
          restoreRecipients();
          setMode('lists');
        }

        // Saved Lists > New List Save Button: Save after creating a new list from the All Contacts tab. Stay on the Saved Lists tab.
        if (mode === 'lists-create-from-recipients') {
          await createList({ name: listName, recipients: selectedRecipients, locationId, type: campaignType });
          setMode('lists');
        }

        // Saving Lists > New List Save Button: Save after after CSV upload. Stay on the Saved Lists tab.
        if (mode === 'lists-create-from-upload') {
          await createList({ name: listName, recipients: selectedRecipients, locationId, type: campaignType });
          restoreRecipients();
          setMode('lists');
        }
      } catch (error) {
        alertError(t('Failed to create list. Please try again.'));
        console.error('Failed to create list:', error);
        sentry.error({
          team: 'fe-messaging',
          topic: 'messages',
          error: 'Error creating contact list',
          severityLevel: 'error',
          addContext: {
            name: 'Network error',
            context: {
              error,
            },
          },
        });
      } finally {
        stopLoading();
      }
    },
    [
      mode,
      expectModeToBe,
      startLoading,
      createList,
      selectedRecipients,
      locationId,
      campaignType,
      restoreRecipients,
      setMode,
      alertError,
      t,
      stopLoading,
    ]
  );

  // Saved Lists > Edit List: Fetch recipients for the selected list. Go to All Contacts tab.
  const startEditList = useCallback(
    async (list: List) => {
      expectModeToBe(['lists'], 'startEditList');
      stashRecipients();
      startLoading();

      try {
        if (!list.listId) {
          throw new Error('Invalid list to edit. Contact list must have a listId.');
        }

        const fetchedList = await getList(list.listId);
        const fetchedRecipients = fetchedList.recipients ?? [];

        setSelectedRecipients(fetchedRecipients);
        setListToUpdate(list);
        setMode('recipients-edit-list');
      } catch (error) {
        restoreRecipients();
        alertError(t('An unexpected error occurred. Please try again.'));
        console.error('Failed to fetch contact list recipients:', error);
        sentry.error({
          team: 'fe-messaging',
          topic: 'messages',
          error: 'Error fetching contact list recipients for edit',
          severityLevel: 'error',
          addContext: {
            name: 'Network error',
            context: {
              error,
            },
          },
        });
      } finally {
        stopLoading();
      }
    },
    [
      expectModeToBe,
      stashRecipients,
      startLoading,
      getList,
      setSelectedRecipients,
      setListToUpdate,
      setMode,
      restoreRecipients,
      alertError,
      t,
      stopLoading,
    ]
  );

  // All Contacts > Edit List Cancel Button: Restore existing recipients. Go to Saved Lists tab.
  const cancelEditList = useCallback(() => {
    expectModeToBe(['recipients-edit-list'], 'cancelEditList');
    restoreRecipients();
    setListToUpdate(undefined);
    setMode('lists');
  }, [expectModeToBe, restoreRecipients, setListToUpdate, setMode]);

  // All Contacts > Edit List Update Button: Save the selected list. Go to Saved Lists tab.
  const finishEditList = useCallback(async () => {
    expectModeToBe(['recipients-edit-list'], 'finishEditList');
    startLoading();

    try {
      if (!listToUpdate) {
        throw new Error('Invalid list to update. No list selected.');
      }

      if (!listToUpdate.listId) {
        throw new Error('Invalid list to update. No listId found.');
      }

      const updatedList: List = {
        ...listToUpdate,
        recipientCount: selectedRecipients.length,
        recipients: selectedRecipients,
      };
      await updateListRecipients(updatedList);

      setListToUpdate(undefined);
      restoreRecipients();
      setMode('lists');
    } catch (error) {
      alertError(t('Failed to update list. Please try again.'));
      console.error('Failed to update list:', error);
      sentry.error({
        team: 'fe-messaging',
        topic: 'messages',
        error: 'Error updating contact list recipients',
        severityLevel: 'error',
        addContext: {
          name: 'Network error',
          context: {
            error,
          },
        },
      });
    } finally {
      stopLoading();
    }
  }, [
    expectModeToBe,
    startLoading,
    listToUpdate,
    selectedRecipients,
    updateListRecipients,
    setListToUpdate,
    restoreRecipients,
    setMode,
    alertError,
    t,
    stopLoading,
  ]);

  // Saved Lists > Rename List: Select the list to rename. Stay on the Saved Lists tab.
  const startRenameList = (list: List) => {
    expectModeToBe(['lists'], 'startRenameList');
    setListToUpdate(list);
    setMode('lists-rename-list');
  };

  // Saved Lists > Rename List Cancel Button: Cancel renaming the list. Stay on the Saved Lists tab.
  const cancelRenameList = () => {
    expectModeToBe(['lists-rename-list'], 'cancelRenameList');
    setListToUpdate(undefined);
    setMode('lists');
  };

  // Saved Lists > Rename List Save Button: Save the renamed list. Stay on the Saved Lists tab.
  const finishRenameList = async (updatedName: string) => {
    expectModeToBe(['lists-rename-list'], 'finishRenameList');
    startLoading();

    try {
      if (!listToUpdate) {
        throw new Error('Invalid list to rename. No list selected.');
      }

      if (!listToUpdate.listId) {
        throw new Error('Invalid list to rename. No listId found.');
      }

      const renamedList: List = {
        ...listToUpdate,
        name: updatedName,
      };
      await renameList(renamedList);

      setListToUpdate(undefined);
      setMode('lists');
    } catch (error) {
      alertError(t('Failed to rename list. Please try again.'));
      console.error('Failed to rename list:', error);
      sentry.error({
        team: 'fe-messaging',
        topic: 'messages',
        error: 'Error renaming contact list',
        severityLevel: 'error',
        addContext: {
          name: 'Network error',
          context: {
            error,
          },
        },
      });
    } finally {
      stopLoading();
    }
  };

  // Saved Lists > Delete List: Select the list to delete and show confirmation modal. Stay on the Saved Lists tab.
  const startDeleteList = (list: List) => {
    // The delete list confirmation modal is opened from the calling context
    expectModeToBe(['lists'], 'startDeleteList');
    setListToUpdate(list);
    setMode('lists-delete-list');
  };

  // Saved Lists > Delete Modal Cancel Button: Cancel deleting the selected list and close confirmation modal. Stay on the Saved Lists tab.
  const cancelDeleteList = () => {
    // The delete list confirmation modal is closed from the calling context
    expectModeToBe(['lists-delete-list'], 'cancelDeleteList');
    setListToUpdate(undefined);
    setMode('lists');
  };

  // Saved Lists > Delete Modal Confirmation Button: Delete the selected list and close confirmation modal. Stay on the Saved Lists tab.
  const finishDeleteList = async () => {
    // The delete list confirmation modal is closed from the calling context
    expectModeToBe(['lists-delete-list'], 'finishDeleteList');
    startLoading();

    try {
      if (!listToUpdate) {
        throw new Error('Invalid list to delete. No list selected.');
      }

      if (!listToUpdate.listId) {
        throw new Error('Invalid list to delete. No listId found.');
      }

      await deleteList(listToUpdate.listId);

      setListToUpdate(undefined);
      setMode('lists');
    } catch (error) {
      alertError(t('Failed to delete list. Please try again.'));
      console.error('Failed to delete list:', error);
      sentry.error({
        team: 'fe-messaging',
        topic: 'messages',
        error: 'Error deleting contact list',
        severityLevel: 'error',
        addContext: {
          name: 'Network error',
          context: {
            error,
          },
        },
      });
    } finally {
      stopLoading();
    }
  };

  return {
    isListSelected,
    lists,
    listsQueryResult,
    selectedListsRecipientsCount,
    toggleList,

    startCreateListFrom,
    cancelCreateList,
    advanceCreateList,
    finishCreateList,

    startDeleteList,
    cancelDeleteList,
    finishDeleteList,

    startEditList,
    cancelEditList,
    finishEditList,

    startRenameList,
    cancelRenameList,
    finishRenameList,
  };
};
